import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormModal } from 'components/containers/FormModal';
import { Select } from '../../../../components/forms/Select';
import {
  GetCategories_workspace_categories,
  GetSocialProfiles_workspace_socialProfiles
} from '../../../../models/legacy-graphql';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { SelectItem } from '../../../../components/forms/Select/Select.view';
import { Loader } from '../../../../components/containers/Loader';
import formClasses from '../../../../components/forms/form.module.scss';
import { CheckboxList } from '../../../../components/forms/CheckboxList';
import { ValidationMessages } from '../../../../constants/validation-messages';
import { SelectAllType } from '../../../../components/forms/CheckboxList/CheckboxList.view';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { getCategorySelectTemplates, MappedCategoriesType } from '../../Categories/Categories.helpers';
import { TimePicker } from '../../../../components/actions/TimePicker';
import { Network, StartOfWeekFormatSetting } from 'models/graphql';

export type AddEditTimeslotFormValues = {
  timeslotId: string;
  ampm: string;
  category: string;
  dayOfWeek: string;
  hourOfDay: string;
  minutesOfHour: string;
  profiles: Array<string | false>;
};

export type AddEditTimeslotModalViewProps = {
  categories: GetCategories_workspace_categories[] | [];
  error: JSX.Element | null;
  isLoading: boolean;
  modalTitle: string;
  onClose: () => void;
  onSubmit: SubmitHandler<AddEditTimeslotFormValues>;
  queryLoading: boolean;
  socialProfiles: GetSocialProfiles_workspace_socialProfiles[] | [];
  filteredProfileId?: string;
  submitted: boolean;
  useFormInstance: UseFormReturn;
  startOfWeek: StartOfWeekFormatSetting;
};

export const getDaysOfWeekItems = (startOfWeek: StartOfWeekFormatSetting): SelectItem[] => {
  const weeksDays =
    startOfWeek === StartOfWeekFormatSetting.SUNDAY
      ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return weeksDays.map(day => ({ label: `${day}s`, value: day.toUpperCase() }));
};

export const AddEditTimeslotModalView = (props: AddEditTimeslotModalViewProps): JSX.Element => {
  let ListOfProfiles = useMemo(
    () =>
      props.socialProfiles?.flatMap(
        (profile: GetSocialProfiles_workspace_socialProfiles, index: number): JSX.Element | JSX.Element[] => {
          const network: Network | null = profilesHelpers.mapNetworkFromTypename(profile.__typename);

          if (!network) {
            return [];
          }

          return (
            <div key={`profile-key-${profile.id}-${index}`} className={'margin-top-small-8 margin-bottom-small-8'}>
              <SocialProfile
                checkbox
                {...(props.filteredProfileId === profile.id ? { checked: true } : {})}
                size={'large'}
                socialProfile={profilesHelpers.remapSocialProfile(profile, false, false, false)}
                label
                sublabel
                disabled={props.submitted}
                loading={props.submitted}
                rules={
                  props.socialProfiles.length === 1
                    ? { required: ValidationMessages.categoriesSocialProfiles.required }
                    : {}
                }
                disconnected={!profile.connected}
                value={profile.id}
                useFormInstance={props.useFormInstance}
                image={profile.photoUrl ? profile.photoUrl : undefined}
                useFormName={`profiles.${index}`}
              />
            </div>
          );
        }
      ),
    [props.filteredProfileId, props.socialProfiles, props.submitted, props.useFormInstance]
  );

  ListOfProfiles = ListOfProfiles.filter(p => p != null);

  const mappedCategories: MappedCategoriesType[] = props.categories
    .filter(item => !item.archived)
    .map(item => {
      const { id, name, color, onboardingContent, defaultSocialProfiles } = item;
      return {
        id,
        name,
        color,
        onboardingContent,
        defaultSocialProfiles
      };
    });

  const categories: SelectItem[] = mappedCategories.map((category: MappedCategoriesType) => ({
    label: category.name,
    value: category.id
  }));

  const daysOfWeekSelectItems = getDaysOfWeekItems(props.startOfWeek);
  const [valueTemplate, itemTemplate] = getCategorySelectTemplates(mappedCategories);

  return (
    <FormModal
      onClose={props.onClose}
      showCloseIcon={!props.submitted}
      formInstance={props.useFormInstance}
      loading={props.submitted}
      onSubmit={props.onSubmit}
      submitText={'Save Timeslot'}
      primaryButtonDisabled={props.queryLoading || props.isLoading}
      title={props.modalTitle}>
      {props.queryLoading ? (
        <Loader variation={'dark'} size={'small'} />
      ) : (
        <div>
          <input type={'hidden'} name={'timeslotId'} />
          <div className={`padding-bottom-small-20`}>
            <Select
              label={'Day of week'}
              items={daysOfWeekSelectItems}
              name={'dayOfWeek'}
              size={'large'}
              rules={{}}
              useFormInstance={props.useFormInstance}
            />
          </div>
          <div className={`padding-bottom-small-20`}>
            <TimePicker
              label={'Time'}
              useFormInstance={props.useFormInstance}
              hoursInputName={'hourOfDay'}
              minutesInputName={'minutesOfHour'}
              ampmInputName={'ampm'}
              size={'large'}
              rules={{ hours: {}, minutes: {}, ampm: {} }}
            />
          </div>
          <div className={`padding-bottom-small-20`}>
            <Select
              label={'Choose a Category'}
              items={categories}
              name={'category'}
              size={'large'}
              rules={{
                required: ValidationMessages.categoriesSelect.required
              }}
              useFormInstance={props.useFormInstance}
              valueTemplate={valueTemplate}
              itemTemplate={itemTemplate}
            />
          </div>
          <label className={formClasses.Label}>Profiles</label>
          <div className={'margin-bottom-small-16'}>
            {props.isLoading ? (
              <div>
                <Loader variation={'light'} size={'small'} type={'inline'} />
              </div>
            ) : (
              <div className={`margin-top-small-8`}>
                <CheckboxList
                  {...((ListOfProfiles.length > 1
                    ? {
                        selectAll: true,
                        checkboxesChildrenGroupName: 'profiles',
                        selectAllSize: 'small',
                        selectAllValidation: { required: ValidationMessages.categoriesSocialProfiles.required }
                      }
                    : {
                        selectAll: false
                      }) as SelectAllType)}
                  label={''}
                  formInstance={props.useFormInstance}
                  disabled={props.submitted}>
                  {ListOfProfiles}
                </CheckboxList>
              </div>
            )}
          </div>
          {props.error ? (
            <div className={'padding-top-small-8'}>
              <BoxNotification type={'warning'}>{props.error}</BoxNotification>
            </div>
          ) : (
            <></>
          )}
        </div>
      )}
    </FormModal>
  );
};
